/**
 * @render react
 * @name Tabs
 * @example
 * <Tabs tabs={[ { title: 'Reliability', content: 'Mei falli impedit ut, eos sumo recteque ei. Prima illum facer ex sit, mea te saperet invenire antiopam.' }, { title: 'Running Costs', content: 'Lorem ipsum dolor sit amet, cu mentitum antiopam praesent has. ' } ]} visibleTabIndex={1} />
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Heading } from 'Components/molecules/Heading';

import './_style.scss';

/**
 * Tabs component
 * @param {array} tabs - Array of objects that contain title string and content string
 * @param {number} visibleTabIndex - Indicates the index number for the visible tab
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */

const Tabs = (
  {
    tabs,
    visibleTabIndex,
    loadStyles,
    extraClassNames,
    context: { cssPrefix, platform },
    showTitle,
  },
) => {
  const [tabIndex, setTabIndex] = useState(visibleTabIndex);

  return (
    <div
      className={classNames({
        [`${cssPrefix}__tabs`]: loadStyles,
        ...extraClassNames,
      })}
    >
      <div className={`${cssPrefix}__tabs--tab-control`}>
        <ul className={`${cssPrefix}__tabs--titles-container`} role="tablist" aria-orientation="horizontal">
          {tabs.map((tab, index) => (
            <li
              key={index}
              className={`${cssPrefix}__tabs--title`}
              role="presentation"
            >
              <a
                className={`${cssPrefix}__tabs--title-link`}
                href={`#tab-${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  setTabIndex(index);
                }}
                aria-controls={`tab-${index}`}
                aria-selected={index === tabIndex}
                role="tab"
                on={platform === 'amp' ? `tap:AMP.setState({ tabIndex: '${index}' })` : undefined}
                data-amp-bind-aria-selected={platform === 'amp' ? `(tabIndex == '${index}' ? 'true' : 'false')` : undefined}
              >
                {tab.title}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className={`${cssPrefix}__tabs--tab-group`}>
        {tabs.map((tab, index) => (
          <div
            key={index}
            className={`${cssPrefix}__tabs--item`}
            role="tabpanel"
            aria-labelledby={`tab-${index}`}
            aria-hidden={index !== tabIndex}
            data-amp-bind-aria-hidden={platform === 'amp' ? `(tabIndex == '${index}' ? 'false' : 'true')` : undefined}
          >
            {showTitle && <Heading size={3}>{tab.title}</Heading>}
            <div className={`${cssPrefix}__tabs--content`}>{tab.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Valid Props
 */
Tabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape()),
  visibleTabIndex: PropTypes.number,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  showTitle: PropTypes.bool,
};

/**
 * Default Props
 */
Tabs.defaultProps = {
  tabs: [],
  visibleTabIndex: 0,
  loadStyles: true,
  extraClassNames: {},
  context: {},
  showTitle: true,
};

export default withPolaris(Tabs);
